import { useContext } from "react";
import { UserDataContext } from "../context/User";

const TIER_ORDER = ["free", "wanderer", "kindred", "quester", "pilgrim", "guardian", "spire"];

const getTierRank = (key) => {
  const rank = TIER_ORDER.indexOf((key || "free").toLowerCase());
  return rank === -1 ? 0 : rank;
};

const useAccess = () => {
  const { userData } = useContext(UserDataContext);

  const isLoggedIn  = !!userData;
  const isAdmin     = userData?.primaryRoleKey === "admin";
  const isReviewer  = userData?.roles?.some((r) => (r.key || r) === "reviewer") || userData?.primaryRoleKey === "reviewer";
  const isMentor    = userData?.roles?.some((r) => (r.key || r) === "mentor") || userData?.primaryRoleKey === "mentor";
  const tierKey     = (userData?.membershipTier?.key || userData?.primaryRoleKey || "free").toLowerCase();
  const tierRank    = getTierRank(tierKey);
  const subStatus   = userData?.subscription?.status;
  const isActive    = isAdmin || tierKey === "free" || ["active", "trialing"].includes(subStatus);
  const isFree      = tierKey === "free";

  // How many quest entries the free user has used this cycle
  const questEntriesUsed = userData?.questEntriesUsedThisCycle || 0;
  const FREE_QUEST_LIMIT = 1;

  const hasAccess = (allowedTiers = []) => {
    if (!isLoggedIn) return false;
    if (isAdmin) return true;
    return allowedTiers.map((t) => t.toLowerCase()).includes(tierKey);
  };

  const meetsMinTier = (minTier) => {
    if (!isLoggedIn) return false;
    if (isAdmin) return true;
    return tierRank >= getTierRank(minTier);
  };

  // ── Feature access rules ────────────────────────────────────────────────────

  // Public — no login needed
  const canAccessGallery    = true;
  const canViewBlogs        = true;   // blogs/newsletters readable by all
  const canViewPolls        = true;   // polls readable by all
  const canViewEvents       = true;   // event calendar readable by all

  // Free+ (login required, free tier included)
  const canAccessProfile    = isLoggedIn;
  const canViewQuiz         = isLoggedIn;                          // free: take self-eval quiz
  const canViewSubmissions  = isLoggedIn;                          // free: view own submissions page
  const canViewGuest        = isLoggedIn;                          // free: view quest/challenge list

  // Quest submission — free gets 1 entry, paid gets unlimited
  const canSubmitQuest      = isLoggedIn && (
    isAdmin ||
    meetsMinTier("wanderer") ||
    (isFree && questEntriesUsed < FREE_QUEST_LIMIT)
  );
  const freeQuestLimitReached = isFree && questEntriesUsed >= FREE_QUEST_LIMIT;

  // Paid only (wanderer+)
  const canAccessShop       = isLoggedIn && meetsMinTier("wanderer") && isActive;
  const canAccessEducation  = isLoggedIn && meetsMinTier("wanderer") && isActive;
  const canAccessCommunity  = isLoggedIn && meetsMinTier("wanderer") && isActive;
  const canAccessWorkshops  = isLoggedIn && meetsMinTier("kindred")  && isActive;
  const canAccessMentoring  = isLoggedIn && meetsMinTier("quester")  && isActive;

  // Projects — kindred+
  const canSubmitProject    = isLoggedIn && meetsMinTier("kindred") && isActive;

  // Awards/Badges — wanderer+
  const canAccessAwards     = isLoggedIn && meetsMinTier("wanderer") && isActive;
  const canAccessBadges     = isLoggedIn && meetsMinTier("wanderer") && isActive;

  // Quiz attempt — free gets access (self-evaluation), wanderer+ gets full quiz
  const canAttemptQuiz      = isLoggedIn;
  const canAccessFullQuiz   = isLoggedIn && meetsMinTier("wanderer") && isActive;

  return {
    isLoggedIn,
    isAdmin,
    isReviewer,
    isMentor,
    tierKey,
    tierRank,
    isActive,
    isFree,
    hasAccess,
    meetsMinTier,
    questEntriesUsed,
    freeQuestLimitReached,
    FREE_QUEST_LIMIT,
    // Feature flags
    canAccessGallery,
    canViewBlogs,
    canViewPolls,
    canViewEvents,
    canAccessProfile,
    canViewQuiz,
    canViewSubmissions,
    canViewGuest,
    canSubmitQuest,
    canAccessShop,
    canAccessEducation,
    canAccessCommunity,
    canAccessWorkshops,
    canAccessMentoring,
    canSubmitProject,
    canAccessAwards,
    canAccessBadges,
    canAttemptQuiz,
    canAccessFullQuiz,
  };
};

export default useAccess;
